<template>
  <ul class="list">
    <li
      v-for="(value, key) in statusData"
      :key="key"
      class="list__item"
      :class="{ total: key === 'Total' }"
    >
      <span class="list__label">
        <div v-if="key !== 'Total'" class="box" :style="{ backgroundColor: value.color }" />
        <span>
          {{ key }}
          <a-tooltip>
            <template #title>
              {{ value.tooltip }}
            </template>
            <InfoCircleOutlined v-if="key !== 'Total' && value.tooltip" :style="{ marginLeft: '2px' }" />
          </a-tooltip>
        </span>
      </span>
      <span class="list__value"> {{ value.count || 0 }} </span>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { computed } from "vue";

import type { IvrData, CallStatus, IvrReturnData } from "@/@types/strategies/dashboard";
import { StrategyType } from "@/app/shared/utils/enums";
import { InfoCircleOutlined } from "@ant-design/icons-vue";

interface CallValue {
  count: number;
  color: string;
}
type StatusData = Record<CallStatus, CallValue>;

const props = defineProps<{ data: IvrData | IvrReturnData; type: Number }>();

const isIvrReturn = computed(() => {
  return props.type === StrategyType.ivrReturn;
});

const statusData = computed<StatusData>(() => {
  let baseData;
  if (isIvrReturn.value) {
    baseData = {
      Contestado: {
        count: props.data?.calls_statistics?.answered || 0,
        color: "#87E8DE",
        tooltip: "Indica cuando un contacto ha contestado la llamada"
      },
      Derivado: {
        count: props.data?.calls_statistics?.derived || 0,
        color: "#D3ADF7",
        tooltip: "Indica cuando un contacto ha sido derivado a un agente"
      },
      "No contesta": {
        count: props.data?.calls_statistics?.no_answered || 0,
        color: "#FFE58F",
        tooltip: "Indica cuando un contacto no contesta la llamada."
      },
      Ocupada: {
        count: props.data?.calls_statistics?.busy || 0,
        color: "#B7EB8F",
        tooltip: 'Indica cuando al llamar, el número devuelve el valor "Ocupado".'
      },
      Fallada: {
        count: props.data?.calls_statistics?.failed || 0,
        color: "#FFA39E",
        tooltip: 'Indica cuando al llamar, el número devuelve el valor "Fallada"..'
      },
      Abandonado: {
        count: props.data?.calls_statistics?.abandoned || 0,
        color: "#EAFF8F",
        tooltip: 'Indica cuando un contacto en cola de espera corta la llamada sin ser atendido.'
      },
      Total: {
        count: 0, color: "transparent" },
  
      };
  } else {
    baseData
    = {
      Contestado: { count: props.data.total_answer, color: "#87E8DE" },
      "No contesta": { count: props.data.total_no_answer, color: "#FFE58F" },
      Ocupada: { count: props.data.total_busy, color: "#B7EB8F" },
      Fallada: { count: props.data.total_failed, color: "#EAFF8F" },
      Total: { count: 0, color: "transparent" },
    };
  }

  let total = 0;

  for (const key in baseData) {
    total += baseData[key as CallStatus].count;
  }

  baseData["Total"].count = total;

  return baseData;
});
</script>

<style lang="sass" scoped>
.list
  padding: 0
  list-style: none
  width: 100%

  &__item
    display: flex
    align-items: center
    justify-content: space-between
    margin: 6px
    padding: 6px 8px
    background-color: $geekblue-1
    border-radius: 8px

    &.total
      background-color: $geekblue-4
      font-weight: 600

  &__label
    font-size: $font-small
    display: flex
    justify-content: center
    align-items: center
    gap: 10px

    .box
      width: 12px
      height: 12px

  &__value
    color: $gray-8
    font-weight: 600
    font-size: $font-medium
</style>
